import { useContext, useRef } from "react";
import { API_BASE } from "../config";
import { track } from "../track";
import type { PreviewDoc } from "../types";
import type { Words } from "./copy";
import { KIND_KEY } from "./kinds";
import { LateLine } from "./Facts";
import { PageContext } from "./page";
import { BillBox, billRows, unbroken } from "./Quantity";
import { BasketContext, useBasketSaysDelivery, type BasketLine } from "./selection";
import { hasCode } from "../types";

/** What a line is called in the order: the product and where it sits on the
 *  page, as its card is headed. */
function named(line: BasketLine, t: Words): string {
  return t.t("pieceNamed", {
    name: t.t(KIND_KEY[line.kind] ?? "kindMinime"),
    n: String(line.piece.at + 1),
    total: String(line.piece.of),
  });
}

/** The order so far, between the cards and the rest of the page: which pieces
 *  are in it, what it comes to and what the parcel saved, and when it arrives
 *  and that nothing more is owed. The same numbers the bar carries and the
 *  sheet it opens shows, from the one bill (selection.ts).
 *
 *  Only on a page that lets them choose (BasketContext.on). Empty it says so
 *  rather than disappearing, so that the place the order will be is known
 *  before there is one. */
export function BasketSummary({ words: t, delivery }: { words: Words; delivery?: PreviewDoc["delivery"] }) {
  const page = useContext(PageContext);
  const info = useContext(BasketContext);
  const says = useBasketSaysDelivery();
  if (!info?.on || (page?.ordered && !page.admin)) return null;
  const { basket } = info;
  const { lines, billed, total, saved } = basket;

  return (
    <div className="card stack" id="order">
      <h2>{t.t("basketTitle")}</h2>
      {!lines.length ? (
        <p className="small muted" style={{ margin: 0 }}>
          {t.t("basketEmpty")}
        </p>
      ) : billed && total && billed.units > 1 ? (
        <BillBox
          rows={billRows(
            lines.map((l) => ({
              key: l.key,
              name: named(l, t),
              size: l.size,
              chip: l.chip,
              offer: l.offer,
              withCode: hasCode(l.offer.code ?? page?.promoCode, l.offer),
              qty: l.qty,
            })),
            billed,
            lines[0]!.offer.format ?? "",
            t
          )}
          total={total}
          save={saved}
          words={t}
        />
      ) : (
        <ul className="together-summary">
          {lines.map((l) => (
            <li key={l.key}>
              <span>
                {named(l, t)} · {unbroken(l.size)}
              </span>
              <strong className="mono">{l.chip.now}</strong>
            </li>
          ))}
          {lines.length > 1 && total && (
            <li className="together-total">
              <span>{t.t("togetherTotal")}</span>
              <strong className="mono">{total}</strong>
            </li>
          )}
        </ul>
      )}
      {/* Which pieces stand on the Christmas base, and their words: free,
          so a line of its own under the sums rather than a row in them. */}
      {lines.some((l) => l.base) && (
        <ul className="basket-bases tiny">
          {lines.map((l) =>
            l.base ? (
              <li key={l.key}>
                {named(l, t)} · {t.t("baseSummary")}
                {l.base.message ? ` · ${t.t("baseQuoted", { message: l.base.message })}` : ""}
              </li>
            ) : null
          )}
        </ul>
      )}
      {lines[0]?.offer.vatNote && (
        <p className="tiny muted" style={{ margin: 0 }}>
          {lines[0].offer.vatNote}
        </p>
      )}
      {/* The one place these lines are said while there is an order
          (useBasketSaysDelivery): the cards below leave them out. */}
      {says && (
        <div className="stack" style={{ gap: 4 }}>
          {delivery?.arrives && (
            <p className="small" style={{ margin: 0 }}>
              {delivery.arrives}
            </p>
          )}
          {delivery?.christmas && (
            <p className="small" style={{ margin: 0, fontWeight: 600 }}>
              {delivery.christmas}
            </p>
          )}
          <LateLine delivery={delivery} language={page?.language ?? "en"} card={false} style={{ margin: 0 }} />
          {/* The duties line verbatim (owner decision, 9 Oct 2026: never
              narrowed or hedged). */}
          <p className="small" style={{ margin: 0 }}>
            {t.t("duties")}
          </p>
        </div>
      )}
    </div>
  );
}

/** The one button on a page of several, parked where the thumb is: what the
 *  order comes to and a way on, or, with nothing chosen yet, a way to the
 *  cards. Continue opens the sheet of "Order them together" on what is
 *  chosen, and tells the server they want to order, once: that used to be a
 *  press on a card's button, and with no such button on the cards it is this
 *  one. Pressing it opens the checkout itself, so no mail goes (orderIntent
 *  mails the Etsy steps only to a page that does not take the payment).
 *
 *  Nothing once it is paid for, and the bar goes with it (the stylesheet
 *  hides an empty one). */
export function BasketBar({ words: t, onContinue, onChoose }: { words: Words; onContinue: () => void; onChoose: () => void }) {
  const page = useContext(PageContext);
  const info = useContext(BasketContext);
  const asked = useRef(false);
  if (!info?.on || (page?.ordered && !page.admin)) return null;
  const { lines, total, units } = info.basket;

  function carryOn() {
    track("basket.continue", { pieces: lines.length, units });
    // Once per visit, and nothing at all from the studio's own look.
    if (!asked.current && page && !page.admin) {
      asked.current = true;
      void fetch(`${API_BASE}/api/order-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: page.token, piece: lines[0]?.piece.piece.id ?? "", from: "basket" }),
      }).catch(() => {
        asked.current = false;
      });
    }
    onContinue();
  }

  if (!lines.length) {
    return (
      <button className="btn btn-primary btn-block btn-lift" onClick={onChoose}>
        {t.t("chooseYours")}
      </button>
    );
  }
  return (
    <button className="btn btn-primary btn-block btn-lift" onClick={carryOn}>
      {total ? `${t.t("togetherContinue")}\u00a0· ${unbroken(total)}` : t.t("togetherContinue")}
    </button>
  );
}
